import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, ChevronDown, ChevronUp, Phone, Mail, MapPin, Inbox, Download, Send, Users, Images } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { usePageMeta } from "../hooks/usePageMeta";
import { AdminApplicants } from "../components/AdminApplicants";
import { AdminSaleGallery } from "../components/AdminSaleGallery";

const STATUSES = ["new", "contacted", "quoted", "completed", "archived"];
const STATUS_STYLES = {
  new: "bg-brass/15 text-brass-hover border-brass/40",
  contacted: "bg-blue-50 text-blue-800 border-blue-200",
  quoted: "bg-forest/10 text-forest border-forest/30",
  completed: "bg-green-50 text-green-800 border-green-200",
  archived: "bg-ink/5 text-ink/50 border-ink/15",
};

function photoUrl(path) {
  const token = localStorage.getItem("macway_admin_token");
  return `${API}/files/${path}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
}

export default function AdminDashboardPage() {
  usePageMeta("Quote Requests | MacWay Admin", "MacWay Estate Services quote request dashboard.");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const loadQuotes = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes", { params: status ? { status } : {} });
      setQuotes(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await api.get("/auth/me");
        setUser(true);
        await loadQuotes("");
      } catch {
        localStorage.removeItem("macway_admin_token");
        navigate("/admin/login", { replace: true });
      } finally {
        setChecking(false);
      }
    })();
  }, [navigate, loadQuotes]);

  const handleFilter = (status) => {
    setFilter(status);
    loadQuotes(status);
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Status updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("macway_admin_token");
    navigate("/admin/login", { replace: true });
  };

  // ---- Estate-sale subscribers ----
  const [tab, setTab] = useState("quotes");
  const [subs, setSubs] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [annSubject, setAnnSubject] = useState("");
  const [annBody, setAnnBody] = useState("");
  const [annConfirm, setAnnConfirm] = useState(false);
  const [annSending, setAnnSending] = useState(false);

  const loadSubscribers = useCallback(async () => {
    setSubsLoading(true);
    try {
      const { data } = await api.get("/subscribers");
      setSubs(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubsLoading(false);
    }
  }, []);

  const activeSubs = subs.filter((s) => s.status === "active");

  const handleExport = async () => {
    try {
      const res = await api.get("/subscribers/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "macway-estate-sale-subscribers.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleAnnounce = async () => {
    if (!annConfirm) {
      setAnnConfirm(true);
      return;
    }
    setAnnSending(true);
    try {
      const { data } = await api.post("/subscribers/announce", { subject: annSubject, body: annBody });
      toast.success(data.message);
      setAnnSubject("");
      setAnnBody("");
      setAnnConfirm(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setAnnConfirm(false);
    } finally {
      setAnnSending(false);
    }
  };

  if (checking || !user) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" data-testid="admin-loading">
        <RefreshCw className="w-8 h-8 text-forest animate-spin" />
      </main>
    );
  }

  return (
    <main className="bg-sand min-h-[70vh] py-10 md:py-14" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-4xl font-medium text-ink" data-testid="admin-heading">Quote Requests</h1>
            <p className="mt-1 text-sm text-ink/60">{quotes.length} request{quotes.length === 1 ? "" : "s"}{filter ? ` · ${filter}` : ""}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => loadQuotes(filter)} className="btn-outline-dark text-xs px-4 py-2.5" data-testid="admin-refresh-button">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={handleLogout} className="btn-primary text-xs px-4 py-2.5" data-testid="admin-logout-button">
              <LogOut className="w-3.5 h-3.5" /> Log out
            </button>
          </div>
        </div>

        <div className="mt-8 flex gap-2" data-testid="admin-tabs">
          <button
            onClick={() => setTab("quotes")}
            className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors ${tab === "quotes" ? "bg-forest text-ivory border-forest" : "bg-white text-ink/60 border-linen hover:border-forest"}`}
            data-testid="admin-tab-quotes"
          >
            Quote Requests
          </button>
          <button
            onClick={() => { setTab("subscribers"); loadSubscribers(); }}
            className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors inline-flex items-center gap-2 ${tab === "subscribers" ? "bg-forest text-ivory border-forest" : "bg-white text-ink/60 border-linen hover:border-forest"}`}
            data-testid="admin-tab-subscribers"
          >
            <Users className="w-3.5 h-3.5" /> Sale Subscribers
          </button>
          <button
            onClick={() => setTab("applicants")}
            className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors inline-flex items-center gap-2 ${tab === "applicants" ? "bg-forest text-ivory border-forest" : "bg-white text-ink/60 border-linen hover:border-forest"}`}
            data-testid="admin-tab-applicants"
          >
            <Users className="w-3.5 h-3.5" /> Applicants
          </button>
          <button
            onClick={() => setTab("gallery")}
            className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider border transition-colors inline-flex items-center gap-2 ${tab === "gallery" ? "bg-forest text-ivory border-forest" : "bg-white text-ink/60 border-linen hover:border-forest"}`}
            data-testid="admin-tab-gallery"
          >
            <Images className="w-3.5 h-3.5" /> Sale Photos
          </button>
        </div>

        {tab === "applicants" && <AdminApplicants />}

        {tab === "gallery" && <AdminSaleGallery />}

        {tab === "quotes" && (
        <>
        <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-status-filters">
          <button
            onClick={() => handleFilter("")}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${filter === "" ? "bg-forest text-ivory border-forest" : "bg-white text-ink/60 border-linen hover:border-forest"}`}
            data-testid="admin-filter-all"
          >
            All
          </button>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => handleFilter(s)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${filter === s ? "bg-forest text-ivory border-forest" : "bg-white text-ink/60 border-linen hover:border-forest"}`}
              data-testid={`admin-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-4">
          {quotes.length === 0 && !loading && (
            <div className="bg-white border border-linen p-14 text-center" data-testid="admin-empty-state">
              <Inbox className="w-10 h-10 text-ink/30 mx-auto" />
              <p className="mt-4 text-ink/60">No quote requests {filter ? `marked "${filter}" ` : ""}yet.</p>
            </div>
          )}

          {quotes.map((q) => {
            const isOpen = expanded === q.id;
            return (
              <article key={q.id} className="bg-white border border-linen" data-testid={`quote-row-${q.id}`}>
                <button
                  className="w-full px-6 py-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-6 text-left hover:bg-sand/60 transition-colors"
                  onClick={() => setExpanded(isOpen ? null : q.id)}
                  data-testid={`quote-row-toggle-${q.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink truncate">
                      {q.name}
                      {q.form_type === "cleanout" && (
                        <span className="ml-2 inline-flex items-center bg-forest/10 text-forest border border-forest/30 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider align-middle" data-testid={`quote-cleanout-badge-${q.id}`}>
                          Cleanout
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-ink/60 truncate">{q.service_needed} · {q.property_location}</p>
                  </div>
                  <div className="text-sm text-ink/60 md:w-40">{q.phone}</div>
                  <div className="text-sm text-ink/50 md:w-36">
                    {q.created_at ? new Date(q.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                  </div>
                  <span className={`inline-flex items-center px-3 py-1 text-xs font-bold uppercase tracking-wider border ${STATUS_STYLES[q.status] || STATUS_STYLES.new}`} data-testid={`quote-status-${q.id}`}>
                    {q.status}
                  </span>
                  {isOpen ? <ChevronUp className="w-5 h-5 text-ink/40" /> : <ChevronDown className="w-5 h-5 text-ink/40" />}
                </button>

                {isOpen && (
                  <div className="border-t border-linen px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid={`quote-detail-${q.id}`}>
                    <dl className="space-y-3 text-sm">
                      <div className="flex items-center gap-2 text-ink/80"><Phone className="w-4 h-4 text-brass" /><a href={`tel:${q.phone}`} className="hover:underline">{q.phone}</a><span className="text-ink/40">· prefers {q.preferred_contact}</span></div>
                      {q.email && <div className="flex items-center gap-2 text-ink/80"><Mail className="w-4 h-4 text-brass" /><a href={`mailto:${q.email}`} className="hover:underline">{q.email}</a></div>}
                      <div className="flex items-center gap-2 text-ink/80"><MapPin className="w-4 h-4 text-brass" />{q.property_location}</div>
                      <div><dt className="font-semibold text-ink">Service</dt><dd className="text-ink/70">{q.service_needed}</dd></div>
                      {q.urgency && <div><dt className="font-semibold text-ink">How soon</dt><dd className="text-ink/70">{q.urgency}</dd></div>}
                      {q.property_size && <div><dt className="font-semibold text-ink">Property size</dt><dd className="text-ink/70">{q.property_size}</dd></div>}
                      <div><dt className="font-semibold text-ink">Timeframe</dt><dd className="text-ink/70">{q.timeframe}</dd></div>
                      {q.referral_source && <div><dt className="font-semibold text-ink">Heard about us via</dt><dd className="text-ink/70">{q.referral_source}</dd></div>}
                      {q.description && <div><dt className="font-semibold text-ink">Their words</dt><dd className="text-ink/70 leading-relaxed">{q.description}</dd></div>}
                    </dl>
                    <div>
                      <label htmlFor={`status-${q.id}`} className="form-label">Update status</label>
                      <select
                        id={`status-${q.id}`}
                        className="form-input"
                        value={q.status}
                        onChange={(e) => handleStatusChange(q.id, e.target.value)}
                        data-testid={`quote-status-select-${q.id}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      {q.photos && q.photos.length > 0 && (
                        <div className="mt-5">
                          <p className="form-label">Photos ({q.photos.length})</p>
                          <div className="flex flex-wrap gap-3" data-testid={`quote-photos-${q.id}`}>
                            {q.photos.map((ph) => (
                              <a key={ph.path} href={photoUrl(ph.path)} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={photoUrl(ph.path)}
                                  alt={ph.original_filename || "Customer photo"}
                                  className="w-24 h-24 object-cover border border-linen hover:border-forest transition-colors"
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
        </>
        )}

        {tab === "subscribers" && (
          <div className="mt-6 space-y-8" data-testid="subscribers-panel">
            <div className="bg-white border border-linen">
              <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-linen">
                <div>
                  <h2 className="text-2xl font-medium text-ink">Estate Sale Subscribers</h2>
                  <p className="mt-1 text-sm text-ink/60" data-testid="subscribers-count">
                    {activeSubs.length} active · {subs.length - activeSubs.length} unsubscribed
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={loadSubscribers} className="btn-outline-dark text-xs px-4 py-2.5" data-testid="subscribers-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${subsLoading ? "animate-spin" : ""}`} /> Refresh
                  </button>
                  <button onClick={handleExport} className="btn-primary text-xs px-4 py-2.5" data-testid="subscribers-export">
                    <Download className="w-3.5 h-3.5" /> Export CSV
                  </button>
                </div>
              </div>

              {subs.length === 0 && !subsLoading && (
                <div className="p-12 text-center" data-testid="subscribers-empty">
                  <Inbox className="w-10 h-10 text-ink/30 mx-auto" />
                  <p className="mt-4 text-ink/60">No subscribers yet. Signups from the Upcoming Estate Sales page appear here.</p>
                </div>
              )}

              {subs.map((s) => (
                <div key={s.id} className="px-6 py-4 border-b border-linen last:border-b-0 flex flex-col md:flex-row md:items-center gap-2 md:gap-6" data-testid={`subscriber-row-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink truncate">{s.email}</p>
                    {s.first_name && <p className="text-sm text-ink/60">{s.first_name}</p>}
                  </div>
                  <div className="text-sm text-ink/60 md:w-44">
                    {s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                  </div>
                  <div className="text-sm text-ink/50 md:w-44 truncate" title={s.consent_text}>
                    {s.consent ? "Consent given" : "—"}
                  </div>
                  <span className={`inline-flex items-center px-3 py-1 text-xs font-bold uppercase tracking-wider border ${s.status === "active" ? "bg-green-50 text-green-800 border-green-200" : "bg-ink/5 text-ink/50 border-ink/15"}`} data-testid={`subscriber-status-${s.id}`}>
                    {s.status}
                  </span>
                </div>
              ))}
            </div>

            <div className="bg-white border border-linen p-7 md:p-9" data-testid="announcement-composer">
              <h2 className="text-2xl font-medium text-ink">Send a Sale Announcement</h2>
              <p className="mt-2 text-sm text-ink/60 leading-relaxed">
                Emails go only to <strong>active subscribers</strong> — unsubscribed addresses are excluded
                automatically, and every email includes a working unsubscribe link. Nothing sends until you
                confirm below.
              </p>
              <div className="mt-6 space-y-5">
                <div>
                  <label htmlFor="ann-subject" className="form-label">Subject</label>
                  <input
                    id="ann-subject"
                    className="form-input"
                    placeholder="e.g. Easton Estate Sale — Dates & Address Announced"
                    value={annSubject}
                    onChange={(e) => { setAnnSubject(e.target.value); setAnnConfirm(false); }}
                    data-testid="announce-subject"
                  />
                </div>
                <div>
                  <label htmlFor="ann-body" className="form-label">Message</label>
                  <textarea
                    id="ann-body"
                    rows={7}
                    className="form-input resize-y"
                    placeholder="Write the announcement in plain text — each line becomes a short paragraph. Paste the Estatesales.net link here if you have one."
                    value={annBody}
                    onChange={(e) => { setAnnBody(e.target.value); setAnnConfirm(false); }}
                    data-testid="announce-body"
                  />
                </div>

                {(annSubject.trim() || annBody.trim()) && (
                  <div className="bg-sand border border-linen p-6" data-testid="announce-preview">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-forest mb-3">Preview</p>
                    <p className="font-semibold text-ink">{annSubject || "(no subject)"}</p>
                    <div className="mt-3 space-y-3">
                      {annBody.split("\n").filter((p) => p.trim()).map((p, i) => (
                        <p key={i} className="text-sm text-ink/70 leading-relaxed">{p}</p>
                      ))}
                    </div>
                    <p className="mt-4 pt-3 border-t border-linen text-xs text-ink/50">Footer will include: unsubscribe link + MacWay branding</p>
                  </div>
                )}

                <button
                  onClick={handleAnnounce}
                  disabled={annSending || !annSubject.trim() || !annBody.trim() || activeSubs.length === 0}
                  className={`${annConfirm ? "btn-brass" : "btn-primary"} disabled:opacity-50`}
                  data-testid="announce-send"
                >
                  <Send className="w-4 h-4" />
                  {annSending
                    ? "Sending…"
                    : annConfirm
                      ? `Confirm — send to ${activeSubs.length} subscriber${activeSubs.length === 1 ? "" : "s"}`
                      : `Send to ${activeSubs.length} active subscriber${activeSubs.length === 1 ? "" : "s"}`}
                </button>
                {annConfirm && !annSending && (
                  <p className="text-xs text-brass-hover font-semibold" data-testid="announce-confirm-note">
                    Review the preview above — click the button again to actually send.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
